import { useNavigate } from "react-router";
import { MUTATIONS, useMutation } from "../../../api";
import Layout from "../../../design-system/components/Layout/Layout.jsx";
import { ShellContent } from "../../shell/Shell.jsx";
import tokens from "../../../design-system/tokens.js";
import NotAuthorizedPage from "../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import ChecklistItem from "../../../design-system/components/Checkbox/ChecklistItem.jsx";
import Button from "../../../design-system/components/Button/Button.jsx";
import Select from "../../../design-system/components/Select/Select.jsx";
import { ROUTES } from "../../../routes.js";
import Header from "../../../design-system/components/Header/Header.jsx";
import { useSparkContext } from "../context/SparkContext.jsx";
import WorkshopDeleteButton from "./components/WorkshopDeleteButton/WorkshopDeleteButton.jsx";
import styles from "./OptionsOverview.css.ts";

/**
 * A component that provides an overview of various options for a workshop, including settings for enabling/ disabling library, and comments, as well as sorting mode for projects.
 *
 * @param {Object} props The component properties.
 * @param {Object} props.workshop The workshop data.
 * @param {Object} props.workshopSettings The current settings of the workshop.
 * @param {Function} props.handleLibraryEnabledChange Callback function to handle changes to the library enabled setting.
 * @param {Function} props.handleCommentsEnabledChange Callback function to handle changes to the comments enabled setting.
 * @param {Function} props.handleProjectSortModeChange Callback function to handle changes to the project sort mode.
 * @returns {JSX.Element} The rendered component.
 */
const OptionsOverviewWithData = ({
  workshop,
  workshopSettings,
  handleLibraryEnabledChange,
  handleCommentsEnabledChange,
  handleProjectSortModeChange,
}) => {
  const navigate = useNavigate();

  // Reset the User Tour
  const handleTourRestart = () => {
    navigate(
      ROUTES.spark.getUrl({
        workshopSlug: workshop?.slug,
        sparkSlug: workshop?.spark.slug,
        withTour: true,
      }),
    );
  };

  return (
    <>
      <Layout Header={() => <Header title={"Optionen"} back={false} />}>
        <div className={styles.optionsPage}>
          <Button onClick={handleTourRestart} width="fit-content">
            Tour durch die Lernreise erneut starten
          </Button>

          <section className={styles.section}>
            <h2>Lernreise-Einstellungen</h2>

            <ChecklistItem
              name="libraryEnabled"
              label="Bibliothek aktivieren"
              checked={workshopSettings.libraryEnabled}
              onChange={handleLibraryEnabledChange}
            />

            <ChecklistItem
              name="commentsEnabled"
              label="Kommentare im Showroom aktivieren"
              checked={workshopSettings.commentsEnabled}
              onChange={handleCommentsEnabledChange}
            />

            <Select
              label="Sortierung der Projekte im Showroom"
              name="projectSortMode"
              options={[
                {
                  label: "aufsteigend nach Erstellungsdatum",
                  value: "creation_date",
                },
                { label: "Zufällige Reihenfolge", value: "random" },
              ]}
              selected={workshopSettings.projectSortMode}
              onChange={handleProjectSortModeChange}
            />
          </section>

          <section className={styles.section}>
            <h2>Lernreise löschen</h2>

            <p>
              Hier kannst du deine Lernreise löschen. Beachte, dass durch das
              Löschen der Lernreise alle Schüler*innen, die derzeit noch in der
              Lernreise sind, ihre Zugehörigkeit verlieren.
            </p>

            <WorkshopDeleteButton workshop={workshop} />
          </section>
        </div>
      </Layout>
    </>
  );
};

/**
 * OptionsOverview is a React component that is responsible for rendering
 * an overview of various settings for a workshop. It uses several hooks to
 * fetch workshop data and settings, and provides mechanisms to update these settings.
 * The settings include enabling/disabling library, comments, and setting
 * project sort mode.
 */
const OptionsOverview = () => {
  const { workshop, settings } = useSparkContext();

  const [doUpdateWorkshopSettings] = useMutation(
    MUTATIONS.updateWorkshopSettings,
  );

  async function handleLibraryEnabledChange(event) {
    await doUpdateWorkshopSettings({
      workshopId: workshop.id,
      slug: workshop.slug,
      libraryEnabled: event.target.checked,
    });
  }

  async function handleCommentsEnabledChange(event) {
    await doUpdateWorkshopSettings({
      workshopId: workshop.id,
      slug: workshop.slug,
      commentsEnabled: event.target.checked,
    });
  }

  async function handleProjectSortModeChange(event) {
    await doUpdateWorkshopSettings({
      workshopId: workshop.id,
      slug: workshop.slug,
      projectSortMode: event.target.value,
    });
  }

  // workshop & settings come from context and are always present here.

  // If I am not a teacher of this course or an admin, return not allowed.
  if (
    !(workshop.roles?.includes("teacher") || workshop.roles?.includes("admin"))
  ) {
    return <NotAuthorizedPage />;
  }

  return (
    <ShellContent bg={tokens.color.neutral.white}>
      <OptionsOverviewWithData
        workshop={workshop}
        workshopSettings={settings}
        handleLibraryEnabledChange={handleLibraryEnabledChange}
        handleCommentsEnabledChange={handleCommentsEnabledChange}
        handleProjectSortModeChange={handleProjectSortModeChange}
      />
    </ShellContent>
  );
};

export default OptionsOverview;
